import React, { useState, useEffect } from 'react';
import { X, MapPin, Calendar, Save, Loader2, MessageSquare, Clock } from 'lucide-react';
import styles from './Overtime.module.css';
import CustomSelect from '../../../components/Common/Select/CustomSelect';
import CustomDatePicker from '../../../components/Common/DatePicker/CustomDatePicker';
import toast from 'react-hot-toast';
import api from '../../../api/axios';

const OTRequestDrawer = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    project_id: '',
    request_date: '',
    requested_start_time: '18:00',
    requested_end_time: '22:00',
    reason: ''
  });

  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);

  useEffect(() => {
    const fetchSites = async () => {
      try {
        const projRes = await api.get('/projects-dropdown');
        if (projRes.data && projRes.data.data) {
          setSites(projRes.data.data.map(proj => ({
            value: proj.id,
            label: proj.project_name
          })));
        } else if (projRes.data) {
          setSites(projRes.data.map(proj => ({
            value: proj.id,
            label: proj.project_name
          })));
        }
      } catch (error) {
        console.error('Error fetching dropdown choices:', error);
        toast.error('Failed to load project choices');
      }
    };

    if (isOpen) {
      fetchSites();
      setFormData({
        project_id: '',
        request_date: '',
        requested_start_time: '18:00',
        requested_end_time: '22:00',
        reason: ''
      });
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.project_id) {
      toast.error('Please select a project site');
      return;
    }
    if (!formData.request_date) {
      toast.error('Please select a date');
      return;
    }
    if (!formData.reason) {
      toast.error('Please provide a reason');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        project_id: Number(formData.project_id),
        request_date: formData.request_date,
        requested_start_time: formData.requested_start_time,
        requested_end_time: formData.requested_end_time,
        reason: formData.reason
      };

      const response = await api.post('/overtime/request', payload);

      if (response.data && response.data.success) {
        toast.success('Overtime Request submitted successfully!');
        if (onSave) {
          onSave(response.data.data);
        }
        onClose();
      } else {
        toast.error(response.data?.message || 'Failed to submit request');
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles['drawer-backdrop']} onClick={onClose}>
      <div className={styles['drawer-container']} onClick={e => e.stopPropagation()}>
        <div className={styles['drawer-header']}>
          <div className={styles['header-title-wrapper']}>
            <h2>Request Overtime</h2>
            <p>Submit a new overtime request to your supervisor</p>
          </div>
          <button className={styles['close-btn']} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles['drawer-form']}>
          <div className={styles['form-scroll-area']}>
            {/* Project Site Select */}
            <div className={styles['form-group']}>
              <label>
                <MapPin size={16} /> Project Site <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <CustomSelect
                options={sites}
                value={sites.find(site => site.value === formData.project_id)}
                onChange={(option) => setFormData({ ...formData, project_id: option ? option.value : '' })}
                placeholder="Choose project site..."
                isSearchable
              />
            </div>

            {/* Date Selector */}
            <div className={styles['form-group']}>
              <label>
                <Calendar size={16} /> OT Shift Date <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <CustomDatePicker
                selected={formData.request_date}
                onChange={(date) => setFormData({ ...formData, request_date: date ? date.toLocaleDateString('en-CA') : '' })}
                minDate={new Date()}
                placeholderText="Select OT Shift Date"
              />
            </div>

            {/* Expected Timings */}
            <div className={styles['form-row']}>
              <div className={styles['form-group']}>
                <label>
                  <Clock size={16} /> Expected Start Time
                </label>
                <input
                  type="time"
                  className={styles['form-input']}
                  value={formData.requested_start_time}
                  onChange={(e) => setFormData({ ...formData, requested_start_time: e.target.value })}
                />
              </div>
              <div className={styles['form-group']}>
                <label>
                  <Clock size={16} /> Expected End Time
                </label>
                <input
                  type="time"
                  className={styles['form-input']}
                  value={formData.requested_end_time}
                  onChange={(e) => setFormData({ ...formData, requested_end_time: e.target.value })}
                />
              </div>
            </div>

            {/* Reason / Remarks */}
            <div className={styles['form-group']}>
              <label>
                <MessageSquare size={16} /> Reason / Remarks <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                className={styles['form-input']}
                placeholder="Explain the purpose of this overtime shift..."
                rows={4}
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                style={{
                  minHeight: '100px',
                  resize: 'vertical'
                }}
              />
            </div>
          </div>

          <div className={styles['drawer-footer']}>
            <button type="button" className={styles['cancel-btn']} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles['save-btn']} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 size={16} className={styles['spinner']} />
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <Save size={16} />
                  <span>Submit Request</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OTRequestDrawer;
